import { useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/ui/toast';
import { createResourceDeletion } from '@/lib/resourceDeletion';
import type { ResourceReadScope } from '@/lib/types';

/** `name` is the quoted Library label, or a description when the source is unavailable. */
export type ResourceDeleteTarget = { resourceId: string; name: string; version: number };

// The server never says which reference keeps a Resource: it may be another
// coach's private draft or workspace, so the copy stays generic.
const REJECTIONS: Record<string, string> = {
  resource_in_use: 'This Resource is still used, for example by Feedback (drafts included), a derived clip or a coaching note. Remove it there first.',
  resource_archive_required: 'Archive this Resource before deleting it.',
  resource_creator_required: 'Only the coach who saved this Resource can delete it.',
};

export function DeleteResourceDialog({ scope, target, onClose }: {
  scope: ResourceReadScope; target: ResourceDeleteTarget; onClose(): void;
}) {
  const toast = useToast();
  const [command] = useState(() => createResourceDeletion(scope, target.resourceId, target.version));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [rebase, setRebase] = useState<number | null>(null);
  const [settled, setSettled] = useState(false);

  const confirm = async () => {
    if (busy) return;
    if (rebase !== null) { command.rebase(rebase); setRebase(null); }
    setBusy(true); setError(null);
    const outcome = await command.send();
    setBusy(false);
    setUncertain(outcome.status === 'unknown');
    if (outcome.status === 'deleted') { toast.success('Resource deleted'); onClose(); return; }
    if (outcome.status === 'stale') setRebase(outcome.currentVersion);
    setSettled(outcome.status === 'gone' || outcome.status === 'unavailable'
      || (outcome.status === 'stale' && outcome.currentVersion === null));
    setError(outcome.status === 'gone' ? 'This Resource is no longer here. It may already have been deleted.'
      : outcome.status === 'unavailable' ? 'Your team or season changed. Close and try again.'
      : outcome.status === 'stale' ? outcome.currentVersion === null ? 'This Resource changed. Close and try again.'
        : 'This Resource changed since you opened the menu. Confirm again to delete it.'
      : outcome.status === 'unknown' ? 'The delete was not confirmed.'
      : REJECTIONS[outcome.code ?? ''] ?? 'The Resource could not be deleted.');
  };

  return <ConfirmDialog title="Delete Resource?" danger data-testid="resource-delete-dialog" confirmTestId="resource-delete-confirm"
    message={<>
      <p>Delete {target.name} and all its saved versions? Match recordings are not affected. This can’t be undone.</p>
      {error && <p role="alert">{error}</p>}
      {uncertain && <p>Keep this tab open. The delete result has not been confirmed.</p>}
    </>}
    confirmLabel={uncertain ? 'Check original operation' : 'Delete Resource'} confirmDisabled={busy || settled}
    onConfirm={() => void confirm()} onClose={() => { if (!busy) onClose(); }} />;
}
